import { useEffect, useState, useRef } from "react";
import styles from "./header.module.less";
import SlideInMenu from "./components/slide-in-menu/slide-in-menu";
import LanguageSelection from "./components/language-selection/language-selection";
import { TLink, TCategory, TLinkList } from "../../../types/link-types";
import { TLanguage, TLanguageLink } from "../../../types/language-types";
import { m as motion } from "motion/react";
import { useAnimateFadeIn } from "../../../animation/animation";
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import { isMobileDevice } from "../../../helpers";
import Search from "../../elements/search-components/search/search";
import { HeaderButton } from "./components/header-button/header-button";
import { useDebounce } from "../../../hooks/useDebounce";
import { useTranslation } from "react-i18next";
import { useSearchQuery } from "../../../react-query-queries/use-search-query";
import { shouldShowSearchLoader } from "../../../helpers/search-helper";
import LinkWrapper from "../../elements/link-wrapper/link-wrapper";

export type TSiblingSiteInformation = {
  heading: string;
  text: string;
  link: TLink;
};

export type onSearchChange = (searchTerm: string, isOpen: boolean) => void;

export type THeaderProps = {
  logo: string;
  popularLinks?: Array<TLink>;
  menuItems: Array<TCategory | TLink>;
  popularLinksHeading?: string;
  siblingSiteInformation: TSiblingSiteInformation;
  languages: Array<TLanguageLink>;
  currentLanguage: TLanguage;
  currentPageUrl?: string;
  siteId: string;
  recommendedSearchLinks?: Array<TLinkList>;
  masterLanguage?: string;
  hideSearch?: boolean;
  onSearchChange?: onSearchChange;
};

export const Header = ({
  logo,
  popularLinks,
  menuItems,
  popularLinksHeading,
  siblingSiteInformation,
  languages,
  currentLanguage,
  currentPageUrl,
  siteId,
  recommendedSearchLinks,
  masterLanguage,
  hideSearch = false,
  onSearchChange,
}: THeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const fadeInAnimation = useAnimateFadeIn({ whileInView: true, easing: "easeInOut" });
  const scrollAllowedElement = useRef<HTMLDivElement | null>(null);
  const scrollAllowedElementSearch = useRef<HTMLDivElement | null>(null);
  const isDesktop = !isMobileDevice();

  const { t } = useTranslation();

  const debouncedSearchTerm = useDebounce(searchTerm);

  const {
    data: searchResults,
    isLoading: isLoadingPostSearchTerm,
    error,
  } = useSearchQuery(debouncedSearchTerm, currentLanguage, siteId);

  const isLoading = shouldShowSearchLoader(isLoadingPostSearchTerm, searchTerm, debouncedSearchTerm);

  useEffect(() => {
    return () => {
      if (isDesktop) {
        clearAllBodyScrollLocks();
      }
    };
  }, []);

  const openSearch = () => {
    setIsSearchOpen(true);
    typeof onSearchChange === "function" && onSearchChange(searchTerm, true);
    if (scrollAllowedElementSearch && scrollAllowedElementSearch.current && isDesktop) {
      disableBodyScroll(scrollAllowedElementSearch.current);
    }
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    typeof onSearchChange === "function" && onSearchChange(searchTerm, false);
    if (scrollAllowedElementSearch && scrollAllowedElementSearch.current && isDesktop) {
      enableBodyScroll(scrollAllowedElementSearch.current);
    }
  };

  const openMenu = () => {
    if (isSearchOpen) {
      closeSearch();
    }

    setIsMenuOpen(true);
    if (scrollAllowedElement && scrollAllowedElement.current && isDesktop) {
      disableBodyScroll(scrollAllowedElement.current);
    }
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    if (scrollAllowedElement && scrollAllowedElement.current && isDesktop) {
      enableBodyScroll(scrollAllowedElement.current);
    }
  };

  const onChangeSeachTerm = (searchTerm: string) => {
    typeof onSearchChange === "function" && onSearchChange(searchTerm, isSearchOpen);
    setSearchTerm(searchTerm);
  };

  return (
    <header id={"languageSelectionPortalWrapper"} className={styles.wrapper}>
      <div className={[styles.topBar, isMenuOpen ? styles.menuOpen : ""].join(" ")}>
        <motion.div {...fadeInAnimation} className={[styles.innerWrapper, "max-width padded-sides"].join(" ")}>
          <LinkWrapper url={currentLanguage === "sv" ? "/" : `/${currentLanguage}`}>
            <div className={styles.logoWrapper}>
              <img className={styles.logo} src={logo} alt="Preem Logo" />
            </div>
          </LinkWrapper>
          <div className={styles.languageSelectionOpenMenuWrapper}>
            <LanguageSelection
              className={styles.languageSelection}
              currentLanguage={currentLanguage}
              languages={languages}
              hideMobile={true}
            />
            {!hideSearch && (
              <HeaderButton
                ariaLabel={t("HEADER_SEARCH_ARIA_LABEL")}
                isActive={isSearchOpen}
                icon={isSearchOpen ? "ui-icon-close" : "ui-icon-search"}
                onClick={isSearchOpen ? closeSearch : openSearch}
              />
            )}
            <HeaderButton
              ariaLabel={t("HEADER_MENU_ARIA_LABEL")}
              className={styles.menuButton}
              noSeparator={true}
              icon={isMenuOpen ? "ui-icon-close" : "ui-icon-menu"}
              onClick={isMenuOpen ? closeMenu : openMenu}
            />
          </div>
        </motion.div>
      </div>
      <Search
        wrapperClassName={styles.searchWrapper}
        isOpen={isSearchOpen}
        isLoadingSearchResults={isLoading}
        results={searchResults}
        searchTerm={searchTerm}
        hasError={error ? true : false}
        scrollAllowedRef={scrollAllowedElementSearch}
        recommendedLinks={recommendedSearchLinks}
        onChangeSearchTerm={onChangeSeachTerm}
        closeSearchFunc={closeSearch}
      />
      <SlideInMenu
        isOpen={isMenuOpen}
        scrollAllowedRef={scrollAllowedElement}
        currentPageUrl={currentPageUrl}
        currentLanguage={currentLanguage}
        languages={languages}
        menuItems={menuItems}
        closeMenuFunc={closeMenu}
        popularLinks={popularLinks}
        popularLinksHeading={popularLinksHeading}
        siblingSiteInformation={siblingSiteInformation}
        masterLanguage={masterLanguage}
      />
      <div className={[styles.overlay, isMenuOpen ? styles.open : ""].join(" ")} onClick={closeMenu} />
    </header>
  );
};

export default Header;
